/* PackagePanelRenderer.js */

"use strict";
const _ = require("lodash");

class PackagePanelRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("package-panel-gl");
        this._rootElem.component = this._model;

        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners() {
        this._model.on("Shutdown", (this._onShutdown = this._onShutdown.bind(this)));
        this._model.on(
            "PackageSummaryReplaced",
            (this._onPackageSummaryReplaced = this._onPackageSummaryReplaced.bind(this))
        );
        this._rootElem.addEventListener("dblclick", (e) => {
            e.stopPropagation();
            e.preventDefault();
            this._model.launchEditor();
        });
    }
    _unregisterListeners() {
        this._model.removeListener("Shutdown", this._onShutdown);
        this._model.removeListener("PackageSummaryReplaced", this._onPackageSummaryReplaced);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        this._rootElem.innerHTML = "";
        let pkgName = this._model.getName();
        let pkgDescription = this._model.getDescription();
        let repoName = this._model.getRepoName() || "Not available";
        let repoDescription = this._model.getRepoDescription();
        let localSummary = this._model.getLocalPkgDetails();
        let localSemVer = _.get(localSummary, "semVersion");
        let remoteSummary = this._model.getRemotePkgDetails();
        let remoteSemVer = _.get(remoteSummary, "semVersion");

        let containerEl = document.createElement("div");
        containerEl.classList.add("name_info");

        let packageIcon = document.createElement("base-icon-gl");
        packageIcon.dataset.icon = "slnman.package";
        packageIcon.classList.add("package-icon");

        let pNameElem = document.createElement("h1");
        pNameElem.classList.add("package_name");
        pNameElem.innerText = pkgName;
        pNameElem.title = _.isEmpty(pkgDescription) ? pkgName : pkgDescription;

        let rNameElem = document.createElement("h2");
        rNameElem.classList.add("repo_name");
        rNameElem.innerText = ` (${repoName})`;
        rNameElem.title = _.isEmpty(repoDescription) ? repoName : repoDescription;

        let supEl = document.createElement("sup");

        //launch icon
        let launchIcon = document.createElement("base-icon-gl");
        launchIcon.dataset.icon = "slnman.launch";
        launchIcon.addEventListener("click", (e) => {
            e.stopPropagation();
            e.preventDefault();
            this._model.launchEditor();
        });

        let vInfoElem = document.createElement("div");
        vInfoElem.classList.add("version_info");

        this._rootElem.appendChild(packageIcon);
        this._rootElem.appendChild(containerEl);
        containerEl.appendChild(pNameElem);
        containerEl.appendChild(rNameElem);
        containerEl.appendChild(supEl);
        supEl.appendChild(launchIcon);
        this._rootElem.appendChild(vInfoElem);

        //Version Info
        switch (this._model.getDiffDetails()) {
            case "Same":
            case "LocalOnly":
                let latestVersionElem = document.createElement("span");
                latestVersionElem.classList.add("latest_version");
                latestVersionElem.innerHTML = `(up to date)`;
                vInfoElem.title = `Version ${localSemVer} of ${pkgName} is currently installed and is the latest version available.`;
                vInfoElem.appendChild(latestVersionElem);
                break;
            case "LocalNewer":
            case "RemoteNewer":
            case "RemoteOnly":
                //remote version details
                let remoteVerDetailsElem = document.createElement("span");
                remoteVerDetailsElem.classList.add("available_version");
                if (remoteSemVer) {
                    remoteVerDetailsElem.innerHTML = `(<span class="semver bold">${remoteSemVer}</span> is&nbsp;available)`;
                }
                remoteVerDetailsElem.addEventListener("click", (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    this._model.launchEditor({tab: "install"});
                });
                //local version details
                let localVerDetailsElem = document.createElement("span");
                localVerDetailsElem.classList.add("installed_version");
                if (localSemVer) {
                    localVerDetailsElem.innerHTML = `(<span class="semver bold">${localSemVer}</span> is&nbsp;installed)`;
                    vInfoElem.title = `Version ${localSemVer} of ${pkgName} is currently installed, but a newer version is available (${remoteSemVer}).`;
                } else {
                    localVerDetailsElem.innerText = `(not installed)`;
                    vInfoElem.title = `${pkgName} is currently not installed, the latest version available is (${remoteSemVer}).`;
                }
                vInfoElem.appendChild(localVerDetailsElem);
                vInfoElem.appendChild(remoteVerDetailsElem);
                break;
        }

        return Promise.resolve();
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onShutdown() {
        this._shutdown();
    }
    _onPackageSummaryReplaced() {
        this.render();
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = PackagePanelRenderer;
